import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { dateFormat } from '@utils/config'
import { formatDate } from '@utils/date.utils'
import { getTransactionDisplay } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'

export const usePATransactionsFile = (rows: TransactionDto[], walletId?: string) => {
  const { t } = useTranslation()
  const isArabic = useArabicSessionLanguage()
  const { formatNumber, formatMoney } = useFormatNumber()
  const { account } = useAccountReadContext()

  const tableBody = useMemo(() => {
    const header = [
      [
        t('Type'),
        t('Date'),
        t('From'),
        t('To'),
        t('Wallet.Reference number'),
        t('Status'),
        t('Amount'),
        t('Transaction exchange rate'),
      ],
    ]

    const body = rows.map((x) => {
      const { fields, amountSign } = getTransactionDisplay(x, 'paymentAgent', {
        isArabic,
        authId: account?.id,
        walletId,
      })
      const date = x.createdDate ? formatDate(x.createdDate, { formatType: dateFormat }) : ''

      const amount = `${amountSign}${formatMoney(fields.amount, fields.currency as CurrencyType)}`
      const rate = formatNumber(fields.rate)

      const row = [
        x.type.name,
        date,
        fields.from,
        fields.to,
        x.referenceId ?? '',
        x.state.name,
        amount,
        rate,
      ]

      return isArabic ? [...row].reverse() : row
    })

    return isArabic ? [header[0].slice().reverse(), ...body] : [...header, ...body]
  }, [t, rows, isArabic, account?.id, formatMoney, formatNumber, walletId])

  return useGenerateDocumentFile('transactions_report', tableBody)
}
